/* actions.js — Catalogue des corrections et rendu des commandes. Une action est
 * une donnée : { type, target, params, nature, warnings, resolves }. Les
 * commandes sont rendues pour shell POSIX et PowerShell ; l'outil dépend de
 * l'action et du conteneur. Expose self.MediaActions. */
'use strict';

var NATURE_ORDER = ['metadata', 'remux', 'transform'];

var LABELS = {
  lossless: 'sans perte',
  reencodes: 'réencode',
  showCommand: 'Voir la commande',
  command: 'Commande',
  allCommands: 'Toutes les corrections',
  allCommandsNote: 'Les corrections de même nature sont réunies en une commande, dans l’ordre : sans perte d’abord.',
  environment: 'Commandes pour',
  workOnCopy: 'Travailler sur une copie',
  beforeAfter: 'Avant → après',
  copy: 'Copier',
  copyAll: 'Copier la commande',
  copied: 'Copié',
  runIn: 'À lancer dans le dossier du fichier.',
  chooseParam: 'Choisissez une valeur pour obtenir la commande.',
  noAction: 'Aucune action proposée pour cet écart.',
  language: 'Langue',
  keepDefault: 'Piste par défaut',
  container: 'Conteneur cible',
  none: 'absente',
  removed: 'retiré',
  yes: 'oui',
  no: 'non',
  // atelier de correction
  fixesLede: 'Chaque correction dit ce qu’elle change et quel écart elle règle. Réglez les valeurs demandées, puis lancez la commande générée sur votre ordinateur : le fichier ne quitte jamais votre appareil, la correction non plus.',
  step1: 'Corrections à appliquer',
  step2: 'Votre ordinateur',
  step3: 'Commande à lancer',
  include: 'Appliquer',
  resolves: 'Règle',
  toChoose: 'à choisir',
  pending: '{count} correction(s) cochée(s) attendent un choix avant d’entrer dans la commande.',
  noneSelected: 'Aucune correction cochée.',
  toolsNeeded: 'Outils nécessaires',
  install: 'installer',
  howTo: 'Ouvrez un terminal dans le dossier du fichier, collez la commande, validez.',
  resultBeside: 'Le fichier corrigé apparaît à côté de l’original, qui n’est pas modifié.',
  resultInPlace: 'Le fichier est modifié sur place.',
  fixAction: 'Corriger',
  // correction dans le navigateur
  inBrowser: 'Dans ce navigateur',
  inBrowserNote: 'Ces corrections ne changent que quelques octets d’en-tête, sans toucher à l’image ni au son. Le fichier corrigé est fabriqué sur votre appareil, puis téléchargé ; l’original n’est pas modifié.',
  makeFile: 'Créer le fichier corrigé',
  making: 'Fabrication…',
  made: 'Fichier corrigé prêt : {name}',
  analyzeFixed: 'Analyser le fichier corrigé',
  copyStep: 'Recopie',
  copyNote: 'Ces corrections se font par recopie des paquets : l’image et le son passent tels quels d’un conteneur à l’autre, sans réencodage ni perte. Le fichier corrigé est fabriqué sur votre appareil.',
  copyExplain: 'le fichier est réécrit en {format}, pistes recopiées telles quelles',
  copyRefused: 'Recopie abandonnée : ce navigateur ne sait pas reprendre {tracks} dans le conteneur d’arrivée. Rien n’a été écrit ; la commande ci-dessous reste possible.',
  covered: 'Couvre {n} des {total} corrections cochées.',
  coveredAll: 'Couvre les {total} corrections cochées.',
  restCommand: 'Les autres demandent la commande ci-dessous.',
  notInBrowser: 'Pas de correction possible dans le navigateur pour ce fichier : {reason}',
  byCommand: 'En ligne de commande',
  seeFix: 'Voir la correction'
};

var TITLES = {
  'set-language': 'Déclarer la langue',
  'set-flag': 'Drapeau « {flag} »',
  'single-default': 'Une seule piste audio par défaut',
  'change-container': 'Passer en {container}',
  'faststart': 'Index en tête du fichier',
  'clean-metadata': 'Retirer les métadonnées personnelles',
  'add-track': 'Ajouter la piste « {label} »',
  'deinterlace': 'Désentrelacer la vidéo',
  'inverse-telecine': 'Rendre au film ses 24 images',
  'constant-fps': 'Cadence d’images constante',
  'reencode': 'Réencoder'
};
var TARGET_NAMES = { video: 'Vidéo', audio: 'Audio', text: 'Sous-titres', image: 'Image' };

var TOOLS = {
  mkvpropedit: { pack: 'MKVToolNix', url: 'https://mkvtoolnix.download/downloads.html',
    install: { posix: 'brew install mkvtoolnix', powershell: 'winget install MoritzBunkus.MKVToolNix' },
    why: 'change les propriétés d’un Matroska sans recopier le fichier : immédiat, même sur plusieurs gigaoctets' },
  mkvmerge: { pack: 'MKVToolNix', url: 'https://mkvtoolnix.download/downloads.html',
    install: { posix: 'brew install mkvtoolnix', powershell: 'winget install MoritzBunkus.MKVToolNix' },
    why: 'écrit le Matroska nativement, avec ses chapitres, ses pièces jointes et ses drapeaux' },
  ffmpeg: { pack: 'FFmpeg', url: 'https://ffmpeg.org/download.html',
    install: { posix: 'brew install ffmpeg', powershell: 'winget install Gyan.FFmpeg' },
    why: 'seul outil à écrire les autres conteneurs et à réencoder' }
};

var ENVIRONMENTS = {
  posix: 'macOS et Linux',
  powershell: 'Windows (PowerShell)'
};

var WARNINGS = {
  'in-place': 'Modifie le fichier original. mkvpropedit n’a pas d’annulation : notez les valeurs avant de lancer la commande.',
  'reencode-video': 'Réencode la vidéo : perte de qualité possible, traitement long.',
  'reencode-audio': 'Réencode l’audio concerné.',
  'container-compat': 'Échoue si un codec n’est pas admis dans le conteneur cible.',
  'container-subs-text': 'Les sous-titres texte sont convertis au format du conteneur cible : la mise en forme des sous-titres ASS (police, position, couleurs) est perdue.',
  'container-subs-image': 'Les sous-titres image (PGS, VobSub) n’ont pas de place dans ce conteneur : ils ne sont pas recopiés.',
  'container-strips': 'Les pièces jointes (polices) et les noms de pistes ne sont pas recopiés.',
  'strips-global': 'Retire toutes les métadonnées générales du fichier (titre, dates, étiquettes), pas seulement les éléments personnels. Les langues et titres des pistes sont conservés ; les pistes de données (position image par image des iPhone, par exemple) ne sont pas recopiées.',
  'strips-global-mkv': 'Retire les étiquettes générales et la date du fichier ; le titre et les étiquettes des pistes sont conservés.',
  'sync': 'Les deux versions n’ont pas la même durée : la piste ajoutée peut être décalée. Vérifiez la synchronisation et, au besoin, décalez-la (option --sync de mkvmerge).'
};

// code ISO 639-2 écrit dans le fichier, code ISO 639-1 du modèle
var LANGUAGES = [
  { code: 'fre', iso1: 'fr', label: 'Français' }, { code: 'eng', iso1: 'en', label: 'Anglais' },
  { code: 'spa', iso1: 'es', label: 'Espagnol' }, { code: 'ger', iso1: 'de', label: 'Allemand' },
  { code: 'ita', iso1: 'it', label: 'Italien' }, { code: 'jpn', iso1: 'ja', label: 'Japonais' },
  { code: 'kor', iso1: 'ko', label: 'Coréen' }, { code: 'chi', iso1: 'zh', label: 'Chinois' },
  { code: 'por', iso1: 'pt', label: 'Portugais' }, { code: 'rus', iso1: 'ru', label: 'Russe' },
  { code: 'ara', iso1: 'ar', label: 'Arabe' }, { code: 'dut', iso1: 'nl', label: 'Néerlandais' },
  { code: 'pol', iso1: 'pl', label: 'Polonais' }, { code: 'swe', iso1: 'sv', label: 'Suédois' },
  { code: 'dan', iso1: 'da', label: 'Danois' }, { code: 'nor', iso1: 'no', label: 'Norvégien' },
  { code: 'fin', iso1: 'fi', label: 'Finnois' }, { code: 'tur', iso1: 'tr', label: 'Turc' },
  { code: 'heb', iso1: 'he', label: 'Hébreu' }, { code: 'hin', iso1: 'hi', label: 'Hindi' },
  { code: 'tha', iso1: 'th', label: 'Thaï' }, { code: 'cze', iso1: 'cs', label: 'Tchèque' },
  { code: 'hun', iso1: 'hu', label: 'Hongrois' }, { code: 'gre', iso1: 'el', label: 'Grec' },
  { code: 'ukr', iso1: 'uk', label: 'Ukrainien' }, { code: 'lit', iso1: 'lt', label: 'Lituanien' },
  { code: 'zxx', iso1: null, label: 'Sans contenu linguistique' }, { code: 'und', iso1: null, label: 'Indéterminée' }
];

var CONTAINERS = {
  'Matroska': { label: 'MKV', ext: 'mkv' },
  'MPEG-4': { label: 'MP4', ext: 'mp4' },
  'WebM': { label: 'WebM', ext: 'webm' }
};

// Sous-titres image : aucune place dans un MP4 ni dans un WebM.
var IMAGE_SUBS = { 'PGS': 1, 'VobSub': 1, 'DVB Subtitle': 1, 'Teletext': 1 };
// Format d'arrivée des sous-titres texte, par conteneur.
var SUB_ENCODERS = { 'MPEG-4': 'mov_text', 'WebM': 'webvtt' };

/* Sous-titres recopiables vers un conteneur qui n'accepte que du texte.
 * Si toutes passent, un seul sélecteur suffit ; sinon on les nomme une à une. */
function subtitlePlan(model, container) {
  var subs = ((model && model.tracks) || []).filter(function (t) { return t.kind === 'text'; });
  var keep = subs.filter(function (t) { return !IMAGE_SUBS[t.formatRaw]; });
  var enc = SUB_ENCODERS[container];
  if (!subs.length || !enc) return { maps: [], codec: [], dropped: [] };
  var dropped = subs.filter(function (t) { return IMAGE_SUBS[t.formatRaw]; });
  if (!keep.length) return { maps: [], codec: [], dropped: dropped };
  var maps = keep.length === subs.length ? ['-map', '0:s?'] : [];
  if (!maps.length) keep.forEach(function (t) { maps.push('-map', '0:s:' + (t.ordinal - 1)); });
  return { maps: maps, codec: ['-c:s', enc], dropped: dropped };
}

// Recalculés à chaque changement : certains dépendent du conteneur choisi.
function warningsOf(a, model) {
  var w = a.warnings.slice();
  if (a.type === 'change-container' && SUB_ENCODERS[a.params.container]) {
    var sub = subtitlePlan(model, a.params.container);
    if (sub.codec.length) w.push('container-subs-text');
    if (sub.dropped.length) w.push('container-subs-image');
    w.push('container-strips');
  }
  return w;
}

var VIDEO_ENCODERS = {
  'AVC': { label: 'H.264', args: ['libx264', '-crf', '18', '-preset', 'slow'] },
  'HEVC': { label: 'HEVC', args: ['libx265', '-crf', '20', '-preset', 'slow'] },
  'VP9': { label: 'VP9', args: ['libvpx-vp9', '-crf', '30', '-b:v', '0'] },
  'AV1': { label: 'AV1', args: ['libsvtav1', '-crf', '30'] }
};
var AUDIO_ENCODERS = {
  'AAC': { label: 'AAC', encoder: 'aac', bitrate: '192k' },
  'Opus': { label: 'Opus', encoder: 'libopus', bitrate: '160k' },
  'MPEG Audio': { label: 'MP3', encoder: 'libmp3lame', bitrate: '192k' },
  'AC-3': { label: 'AC-3', encoder: 'ac3', bitrate: '448k' },
  'E-AC-3': { label: 'E-AC-3', encoder: 'eac3', bitrate: '448k' },
  'FLAC': { label: 'FLAC', encoder: 'flac' },
  'PCM': { label: 'PCM', encoder: 'pcm_s24le' }
};

/* ---------- Catalogue des actions ---------- */
var ACTIONS = {
  'set-language': {
    label: 'Définir la langue d’une piste', nature: 'metadata', resolves: ['no-lang'],
    explanation: 'Déclare la langue de la piste. Le son et l’image ne sont pas touchés.'
  },
  'set-flag': {
    label: 'Poser ou retirer un drapeau', nature: 'metadata', resolves: [],
    explanation: 'Pose ou retire le drapeau « {flag} » de la piste. Le son et l’image ne sont pas touchés.'
  },
  'single-default': {
    label: 'Ne garder qu’une piste audio par défaut', nature: 'metadata', resolves: ['multi-default'],
    explanation: 'Ne laisse le drapeau « par défaut » que sur la piste audio choisie. Le son et l’image ne sont pas touchés.'
  },
  'change-container': {
    label: 'Changer de conteneur', nature: 'remux', resolves: ['unusual-combo'],
    explanation: 'Recopie toutes les pistes telles quelles dans un conteneur {container}, sans réencodage.'
  },
  'faststart': {
    label: 'Déplacer le moov en tête', nature: 'remux', resolves: ['moov-late'],
    explanation: 'Place l’index (moov) en tête du fichier : la lecture en ligne démarre sans attendre la fin du téléchargement. Aucune piste n’est réencodée.'
  },
  'clean-metadata': {
    label: 'Nettoyer les métadonnées personnelles', nature: 'metadata', resolves: ['personal-metadata'],
    explanation: 'Retire les métadonnées générales du fichier, dont la position, l’appareil, l’auteur et les commentaires. Le son et l’image ne sont pas touchés.'
  },
  'add-track': {
    label: 'Ajouter une piste venue d’un autre fichier', nature: 'remux', resolves: [],
    explanation: 'Crée une copie de ce fichier qui reçoit en plus la piste « {label} » de « {from} ». Aucune piste n’est réencodée.'
  },
  'deinterlace': {
    label: 'Désentrelacer', nature: 'transform', resolves: [],
    explanation: 'Désentrelace la vidéo (filtre bwdif) et la réencode en {video}. L’audio est recopié tel quel.'
  },
  'inverse-telecine': {
    label: 'Défaire le télécinéma (3:2)', nature: 'transform', resolves: ['telecine-content'],
    explanation: 'Réapparie les demi-images (filtre fieldmatch), écarte celles qui ont été ajoutées pour passer de 24 à 30 images (filtre decimate), et réencode en {video}. Le film retrouve ses 24 images par seconde d’origine, sans le flou qu’un désentrelacement laisserait. L’audio est recopié tel quel.'
  },
  'constant-fps': {
    label: 'Passer en cadence constante', nature: 'transform', resolves: [],
    explanation: 'Réencode la vidéo en {video} à cadence constante, {fps} i/s.'
  },
  'reencode': {
    label: 'Réencoder vers le format attendu', nature: 'transform', resolves: ['high-bitrate'],
    explanation: 'Réencode les pistes concernées ({detail}) ; les autres pistes sont recopiées telles quelles.'
  }
};

var FLAG_LABELS = { default: 'par défaut', forced: 'forcé' };

function fill(tpl, params) {
  return String(tpl).replace(/\{(\w+)\}/g, function (m, k) { return params && params[k] != null ? String(params[k]) : m; });
}

/* ---------- Construction des actions ---------- */
function make(type, target, params) {
  var a = ACTIONS[type];
  var action = { type: type, target: target, params: params || {}, nature: a.nature, warnings: [], resolves: a.resolves.slice() };
  if (type === 'deinterlace' || type === 'inverse-telecine' || type === 'constant-fps' || (type === 'reencode' && params.video)) action.warnings.push('reencode-video');
  if (type === 'reencode' && params.audio && params.audio.length) action.warnings.push('reencode-audio');
  if (type === 'change-container' && params.container !== 'Matroska') action.warnings.push('container-compat');
  return action;
}

function langCode(iso1) {
  for (var i = 0; i < LANGUAGES.length; i++) if (LANGUAGES[i].iso1 === iso1) return LANGUAGES[i].code;
  return null;
}

function firstAllowed(expected, known) {
  var v = expected.value;
  var list = Array.isArray(v) ? v : [v];
  for (var i = 0; i < list.length; i++) if (known[list[i]]) return list[i];
  return null;
}

/* ctx : { model, failing }. Paramètres préremplis quand l'écart les donne,
 * laissés vides sinon. */
function propose(gap, ctx) {
  var model = ctx.model;
  if (gap.type === 'no-lang') return [make('set-language', gap.subject, { language: null })];
  if (gap.type === 'multi-default') {
    return [make('single-default', { kind: 'container' }, {
      keep: gap.observed.length ? gap.observed[0].subject.ordinal : null,
      tracks: gap.observed.map(function (o) { return o.subject.ordinal; })
    })];
  }
  if (gap.type === 'unusual-combo') return [make('change-container', { kind: 'container' }, { container: 'Matroska' })];
  if (gap.type === 'moov-late') return [make('faststart', { kind: 'container' }, {})];
  if (gap.type === 'personal-metadata') {
    // ffmpeg refuse un type de piste absent du fichier
    var kinds = ['video', 'audio', 'text'].filter(function (k) { return model.tracks.some(function (t) { return t.kind === k; }); });
    var clean = make('clean-metadata', { kind: 'container' }, { removed: gap.observed.map(function (o) { return o.property; }), kinds: kinds });
    clean.warnings.push(model.container.formatRaw === 'Matroska' ? 'strips-global-mkv' : 'strips-global');
    return [clean];
  }
  if (gap.type === 'high-bitrate') {
    var src = trackOf(model, gap.subject);
    var enc = src && VIDEO_ENCODERS[src.formatRaw] ? src.formatRaw : 'AVC';
    return [make('reencode', { kind: 'container' }, { video: enc, audio: [] })];
  }
  if (gap.type === 'interlaced-content') return [make('deinterlace', { kind: 'container' }, { video: sourceEncoder(model) })];
  if (gap.type === 'telecine-content') return [make('inverse-telecine', { kind: 'container' }, { video: sourceEncoder(model) })];
  if (gap.type === 'profile-rule') return fromRule(gap, ctx);
  return [];
}

function fromRule(gap, ctx) {
  var e = gap.expected;
  if (!e) return [];
  var failing = (ctx.failing || []).filter(function (s) { return s.kind !== 'container'; });
  var FILE = { kind: 'container' };

  if (e.field === 'language.code' && failing.length) {
    var lang = (e.op === 'eq' || (e.op === 'inList' && e.value.length === 1)) ? langCode(Array.isArray(e.value) ? e.value[0] : e.value) : null;
    return failing.map(function (s) { return make('set-language', s, { language: lang }); });
  }
  if ((e.field === 'default' || e.field === 'forced') && e.op === 'eq' && typeof e.value === 'boolean' && failing.length) {
    return failing.map(function (s) { return make('set-flag', s, { flag: e.field, value: e.value }); });
  }
  if (e.track === 'container' && e.field === 'formatRaw') {
    var target = firstAllowed(e, CONTAINERS);
    return target ? [make('change-container', FILE, { container: target })] : [];
  }
  if (e.track === 'video' && e.field === 'scanType' && e.op === 'eq' && e.value === 'progressive') {
    return [make('deinterlace', FILE, { video: sourceEncoder(ctx.model) })];
  }
  if (e.track === 'video' && e.field === 'frameRateMode' && e.op === 'eq' && e.value === 'CFR') {
    var v = firstVideo(ctx.model);
    return v && v.frameRate ? [make('constant-fps', FILE, { video: sourceEncoder(ctx.model), fps: v.frameRate })] : [];
  }
  if (e.track === 'video' && e.field === 'formatRaw') {
    var vc = firstAllowed(e, VIDEO_ENCODERS);
    return vc ? [make('reencode', FILE, { video: vc, audio: [] })] : [];
  }
  if (e.track === 'audio' && failing.length) {
    var opts = audioOptions(e);
    if (!opts) return [];
    return [make('reencode', FILE, { video: null, audio: failing.map(function (s) {
      var t = trackOf(ctx.model, s);
      return Object.assign({ ordinal: s.ordinal, codec: opts.codec || (t && AUDIO_ENCODERS[t.formatRaw] ? t.formatRaw : 'AAC') }, opts.extra);
    }) })];
  }
  return [];
}

// null si la règle ne se corrige pas par un réglage audio.
function audioOptions(e) {
  var list = Array.isArray(e.value) ? e.value : [e.value];
  if (e.field === 'formatRaw' && (e.op === 'eq' || e.op === 'inList')) {
    var c = firstAllowed(e, AUDIO_ENCODERS);
    return c ? { codec: c, extra: {} } : null;
  }
  if (e.field === 'samplingRate' && (e.op === 'eq' || e.op === 'inList' || e.op === 'gte')) {
    var sr = list.indexOf(48000) >= 0 ? 48000 : list[0];
    return { codec: null, extra: { sampleRate: sr } };
  }
  if (e.field === 'channels' && (e.op === 'eq' || e.op === 'inList' || e.op === 'lte')) {
    var ch = list.indexOf(2) >= 0 ? 2 : list[0];
    return { codec: null, extra: { channels: ch } };
  }
  if (e.field === 'bitRate' && e.op === 'gte') return { codec: null, extra: { bitrate: Math.ceil(e.value / 1000) + 'k' } };
  return null;
}

function firstVideo(model) {
  for (var i = 0; i < model.tracks.length; i++) if (model.tracks[i].kind === 'video') return model.tracks[i];
  return null;
}
function sourceEncoder(model) {
  var v = firstVideo(model);
  return v && VIDEO_ENCODERS[v.formatRaw] ? v.formatRaw : 'AVC';
}
function trackOf(model, subject) {
  for (var i = 0; i < model.tracks.length; i++) {
    var t = model.tracks[i];
    if (t.kind === subject.kind && t.ordinal === subject.ordinal) return t;
  }
  return null;
}

function ready(a) {
  if (a.type === 'set-language') return !!a.params.language;
  if (a.type === 'single-default') return a.params.keep != null;
  return true;
}

/* ---------- Ce que la recopie des paquets sait faire ----------
 * Déclaré ici, sans dépendance : convert.js ne se charge qu'à l'exécution. */
var COPY_ACTIONS = { 'change-container': 1, 'faststart': 1, 'clean-metadata': 1 };
var COPY_CONTAINERS = {
  'MPEG-4': { label: 'MP4', ext: 'mp4', mp4Family: true },
  'QuickTime': { label: 'MOV', ext: 'mov', mp4Family: true },
  'Matroska': { label: 'MKV', ext: 'mkv' },
  'WebM': { label: 'WebM', ext: 'webm' }
};
var COPY_REASONS = {
  container: 'Le conteneur de départ ou d’arrivée n’est pas écrit par le navigateur.',
  none: 'Aucune des corrections cochées ne se fait par recopie.',
  tracks: 'Une piste n’a pas pu être reprise telle quelle dans le conteneur d’arrivée.'
};

function copyHandles(action) { return !!COPY_ACTIONS[action.type]; }

// -> { actions, from, container, label, ext, fastStart, dropTags }
function copyPlan(actions, model) {
  var from = model && model.container ? model.container.formatRaw : null;
  var mine = (actions || []).filter(copyHandles);
  if (!mine.length) return { actions: [], reason: COPY_REASONS.none };
  var container = from;
  mine.forEach(function (a) { if (a.type === 'change-container') container = a.params.container; });
  if (!COPY_CONTAINERS[from] || !COPY_CONTAINERS[container]) return { actions: [], reason: COPY_REASONS.container };
  var wantsFastStart = mine.some(function (a) { return a.type === 'faststart'; }) ||
    !!(COPY_CONTAINERS[container].mp4Family && mine.some(function (a) { return a.type === 'change-container'; }));
  return {
    actions: mine, from: from, container: container,
    label: COPY_CONTAINERS[container].label, ext: COPY_CONTAINERS[container].ext,
    fastStart: wantsFastStart && !!COPY_CONTAINERS[container].mp4Family,
    dropTags: mine.some(function (a) { return a.type === 'clean-metadata'; })
  };
}

/* ---------- Explication, valeurs avant et après ---------- */
function explain(a) {
  var p = a.params;
  var detail = [];
  if (a.type === 'reencode') {
    if (p.video) detail.push('vidéo en ' + VIDEO_ENCODERS[p.video].label);
    (p.audio || []).forEach(function (x) {
      var d = 'audio ' + x.ordinal + ' en ' + AUDIO_ENCODERS[x.codec].label;
      if (x.sampleRate) d += ' ' + (x.sampleRate / 1000).toLocaleString('fr-FR') + ' kHz';
      if (x.channels) d += ' ' + x.channels + ' canaux';
      if (x.bitrate) d += ' ' + x.bitrate.replace('k', ' kb/s');
      detail.push(d);
    });
  }
  return fill(ACTIONS[a.type].explanation, {
    label: p.label, from: p.from,
    flag: FLAG_LABELS[p.flag], container: p.container && CONTAINERS[p.container].label,
    video: p.video && VIDEO_ENCODERS[p.video].label, fps: p.fps != null ? String(p.fps).replace('.', ',') : null,
    detail: detail.join(', ')
  });
}

function title(a) {
  var p = a.params, t = a.target || {};
  var head = fill(TITLES[a.type] || a.type, { flag: FLAG_LABELS[p.flag], container: p.container && CONTAINERS[p.container].label, label: p.label });
  var name = TARGET_NAMES[t.kind];
  return name && a.type !== 'single-default' && a.type !== 'add-track' ? head + ' — ' + name + (t.ordinal ? ' ' + t.ordinal : '') : head;
}

function yesNo(v) { return v === true ? LABELS.yes : v === false ? LABELS.no : LABELS.none; }

function beforeAfter(a, model) {
  var p = a.params;
  if (a.type === 'set-language') {
    var t = trackOf(model, a.target);
    var before = t && t.language && t.language.raw ? t.language.raw : LABELS.none;
    return [LABELS.language + ' : ' + before + ' → ' + (p.language || '?')];
  }
  if (a.type === 'set-flag') {
    var tf = trackOf(model, a.target);
    return [FLAG_LABELS[p.flag] + ' : ' + yesNo(tf ? tf[p.flag] : null) + ' → ' + yesNo(p.value)];
  }
  if (a.type === 'clean-metadata') {
    return (model.privacy || []).filter(function (x) { return p.removed.indexOf('privacy.' + x.kind) >= 0; }).map(function (x) {
      return x.value + ' → ' + LABELS.removed;
    });
  }
  if (a.type === 'single-default') {
    return p.tracks.map(function (n) {
      return 'Audio ' + n + ', ' + FLAG_LABELS.default + ' : ' + LABELS.yes + ' → ' + (n === p.keep ? LABELS.yes : LABELS.no);
    });
  }
  return [];
}

/* ---------- Échappement ---------- */
function quotePosix(s) { return "'" + String(s).replace(/'/g, "'\\''") + "'"; }
// PowerShell prend aussi les apostrophes typographiques pour des délimiteurs.
function quotePowerShell(s) { return "'" + String(s).replace(/['‘’‚‛]/g, function (c) { return c + c; }) + "'"; }

var SAFE = {
  posix: /^[A-Za-z0-9_\-.:=+,/@%]+$/,
  powershell: /^-{0,2}[A-Za-z0-9_]+$/
};

function token(t, shell) {
  if (t && typeof t === 'object') return quote(fileArg(t.file), shell); // nom de fichier
  return SAFE[shell].test(t) ? t : quote(t, shell);
}
function quote(s, shell) { return shell === 'powershell' ? quotePowerShell(s) : quotePosix(s); }
// « ./ » : un nom en « - » ou contenant « : » ne passe ni pour une option ni
// pour un protocole ffmpeg.
function fileArg(name) { return './' + name; }

// Chemin absolu, sans réinterprétation du nom par PowerShell.
function psPath(name) { return '[System.IO.Path]::Combine($PWD.ProviderPath, ' + quotePowerShell(name) + ')'; }

function line(tokens, shell) { return tokens.map(function (t) { return token(t, shell); }).join(' '); }

/* ---------- Noms de fichiers ---------- */
function splitName(name) {
  var i = name.lastIndexOf('.');
  return i > 0 ? { base: name.slice(0, i), ext: name.slice(i + 1) } : { base: name, ext: '' };
}
function outputName(name, ext, suffix) {
  var n = splitName(name);
  var e = ext || n.ext;
  return n.base + suffix + (e ? '.' + e : '');
}

/* ---------- Choix de l'outil ----------
 * Trois étages, le plus bas qui suffit l'emporte : sur place (mkvpropedit, pour
 * les propriétés d'un Matroska), remux (mkvmerge vers Matroska, qu'il est seul
 * à écrire nativement et seul à savoir écrire ; ffmpeg sinon), transformation
 * (ffmpeg). Une correction peut enchaîner deux outils. */
function toolFor(nature, from, to, list) {
  if (nature === 'metadata') return from === 'Matroska' ? 'mkvpropedit' : 'ffmpeg';
  if (nature === 'remux') return to === 'Matroska' ? 'mkvmerge' : 'ffmpeg';
  return 'ffmpeg';
}

/* ---------- Plan des commandes ----------
 * Les actions de même nature font une commande ; les natures s'enchaînent dans
 * l'ordre métadonnées, remux, transformation. ctx : { fileName, model, copy } */
// Une correction proposée par deux écarts n'est gardée qu'une fois.
function actionKey(a) {
  var t = a.target || {};
  return [a.type, t.kind, t.ordinal, a.params.flag || '', a.type === 'reencode' || a.type === 'change-container' ? JSON.stringify(a.params) : ''].join('|');
}

function plan(actions, ctx) {
  var seen = {};
  var ready_ = actions.filter(ready).filter(function (a) {
    var k = actionKey(a);
    if (seen[k]) return false;
    seen[k] = true;
    return true;
  });
  var byNature = {};
  ready_.forEach(function (a) { (byNature[a.nature] = byNature[a.nature] || []).push(a); });
  var natures = NATURE_ORDER.filter(function (n) { return byNature[n]; });

  /* Un fichier qui devient Matroska : remuxer d'abord, poser les propriétés
   * ensuite avec mkvpropedit. L'ordre habituel le réécrirait deux fois. */
  var finalContainer = null;
  (byNature.remux || []).forEach(function (a) { if (a.type === 'change-container') finalContainer = a.params.container; });
  if (finalContainer === 'Matroska' && ctx.model.container.formatRaw !== 'Matroska' && byNature.metadata) {
    natures = ['remux', 'metadata'].concat(natures.filter(function (n) { return n === 'transform'; }));
  }
  var steps = [];
  var input = ctx.fileName;
  var containerRaw = ctx.model.container.formatRaw;
  var ext = null; // extension de sortie, changée avec le conteneur

  /* Outil et conteneur de chaque étape : une étape sur place ne crée pas de
   * fichier, donc « -corrige » revient à la dernière qui en crée un. */
  var cont0 = containerRaw, input0 = ctx.fileName;
  var pass = natures.map(function (nature) {
    var list = byNature[nature];
    var cont = cont0;
    list.forEach(function (a) { if (a.type === 'change-container') cont = a.params.container; });
    if (list.some(function (a) { return a.type === 'add-track'; })) cont = 'Matroska';
    var tool = toolFor(nature, cont0, cont, list);
    var creates = !(tool === 'mkvpropedit' && !(ctx.copy && input0 === ctx.fileName));
    if (creates) input0 = null;
    cont0 = cont;
    return { nature: nature, creates: creates };
  });
  var lastCreator = -1;
  pass.forEach(function (x, i) { if (x.creates) lastCreator = i; });

  natures.forEach(function (nature, i) {
    var list = byNature[nature];
    var suffix = i === lastCreator || lastCreator < 0 ? '-corrige' : '-etape' + (i + 1);
    var cont = containerRaw;
    list.forEach(function (a) { if (a.type === 'change-container') cont = a.params.container; });
    var tool = toolFor(nature, containerRaw, cont, list);
    var onOriginal = input === ctx.fileName;
    if (list.some(function (a) { return a.type === 'add-track'; })) cont = 'Matroska';

    if (tool === 'mkvpropedit') {
      var target = input;
      var pre = [];
      if (ctx.copy && onOriginal) {
        target = outputName(ctx.fileName, null, suffix);
        pre.push({ copy: { from: input, to: target } });
      }
      steps.push({ nature: nature, tool: tool, inPlace: !pre.length && onOriginal, pre: pre,
        tokens: ['mkvpropedit', { file: target }].concat(mkvpropeditArgs(list)), output: target });
      input = target;
      return;
    }
    if (cont !== containerRaw) ext = CONTAINERS[cont].ext;
    var out = outputName(ctx.fileName, ext, suffix);
    /* Vers un conteneur qui n'accepte que des sous-titres texte, les pistes
     * sont nommées une à une : « -map 0 » embarquerait pièces jointes et
     * sous-titres image, et ffmpeg refuserait d'écrire. */
    var sub = cont !== containerRaw ? subtitlePlan(ctx.model, cont) : { maps: [], codec: [] };
    var maps;
    if (sub.maps.length || sub.codec.length || (cont !== containerRaw && SUB_ENCODERS[cont])) {
      maps = ['-map', '0:V?', '-map', '0:a?'].concat(sub.maps);
    } else if (list.some(function (a) { return a.type === 'clean-metadata'; })) {
      maps = ['-map', '0:V?', '-map', '0:a?', '-map', '0:s?'];
    } else maps = ['-map', '0'];
    var tokens = tool === 'mkvmerge'
      ? ['mkvmerge', '-o', { file: out }, { file: input }].concat(addTrackArgs(list))
      : ['ffmpeg', '-i', { file: input }].concat(maps, ['-c', 'copy'], sub.codec).concat(ffmpegArgs(list, cont)).concat([{ file: out }]);
    steps.push({ nature: nature, tool: tool, inPlace: false, pre: [], tokens: tokens, output: out });
    input = out;
    containerRaw = cont;
  });
  return steps;
}

// Pistes d'autres fichiers, sans leurs chapitres ni pièces jointes.
function addTrackArgs(list) {
  var bySource = {}, order = [];
  list.forEach(function (a) {
    if (a.type !== 'add-track') return;
    var p = a.params;
    if (!bySource[p.from]) { bySource[p.from] = { audio: [], text: [] }; order.push(p.from); }
    bySource[p.from][p.kind].push(String(p.tid));
  });
  var args = [];
  order.forEach(function (from) {
    var s = bySource[from];
    args.push('--no-video');
    if (s.audio.length) args.push('--audio-tracks', s.audio.join(',')); else args.push('--no-audio');
    if (s.text.length) args.push('--subtitle-tracks', s.text.join(',')); else args.push('--no-subtitles');
    args.push('--no-chapters', '--no-attachments', '--no-global-tags', { file: from });
  });
  return args;
}

function mkvSelector(subject) {
  var letter = { video: 'v', audio: 'a', text: 's' }[subject.kind];
  return 'track:' + letter + subject.ordinal;
}

function mkvpropeditArgs(list) {
  var args = [];
  list.forEach(function (a) {
    var p = a.params;
    if (a.type === 'set-language') args.push('--edit', mkvSelector(a.target), '--set', 'language=' + p.language);
    else if (a.type === 'set-flag') args.push('--edit', mkvSelector(a.target), '--set', 'flag-' + p.flag + '=' + (p.value ? 1 : 0));
    else if (a.type === 'single-default') {
      p.tracks.forEach(function (n) {
        args.push('--edit', 'track:a' + n, '--set', 'flag-default=' + (n === p.keep ? 1 : 0));
      });
    }
    else if (a.type === 'clean-metadata') args.push('--tags', 'global:', '--edit', 'info', '--delete', 'date');
  });
  return args;
}

// « V » exclut les pochettes ; index à partir de 0.
function ffSpec(subject) {
  var letter = { video: 'V', audio: 'a', text: 's' }[subject.kind];
  return letter + ':' + (subject.ordinal - 1);
}

function ffmpegArgs(list, container) {
  var args = [];
  var filters = [], video = null, fps = null, audio = [];
  list.forEach(function (a) {
    var p = a.params;
    if (a.type === 'set-language') args.push('-metadata:s:' + ffSpec(a.target), 'language=' + p.language);
    else if (a.type === 'set-flag') args.push('-disposition:' + ffSpec(a.target), p.value ? p.flag : '0');
    else if (a.type === 'single-default') {
      p.tracks.forEach(function (n) { args.push('-disposition:a:' + (n - 1), n === p.keep ? 'default' : '0'); });
    }
    else if (a.type === 'faststart') args.push('-movflags', '+faststart');
    else if (a.type === 'clean-metadata') {
      args.push('-map_metadata', '-1');
      (p.kinds || []).forEach(function (k) {
        var sp = { video: 'V', audio: 'a', text: 's' }[k];
        args.push('-map_metadata:s:' + sp, '0:s:' + sp);
      });
      args.push('-fflags', '+bitexact');
    }
    else if (a.type === 'deinterlace') { filters.push('bwdif=mode=send_frame'); video = video || p.video; }
    // fieldmatch réapparie, yadif rattrape le reste, decimate retire l'excédent
    else if (a.type === 'inverse-telecine') {
      filters.push('fieldmatch', 'yadif=deint=interlaced', 'decimate');
      video = video || p.video;
    }
    else if (a.type === 'constant-fps') { fps = p.fps; video = video || p.video; }
    else if (a.type === 'reencode') {
      if (p.video) video = p.video;
      (p.audio || []).forEach(function (x) { audio.push(x); });
    }
  });
  if (filters.length) args.push('-filter:V', filters.join(','));
  if (fps != null) args.push('-fps_mode:V', 'cfr', '-r:V', String(fps));
  if (video) { args.push('-c:V'); Array.prototype.push.apply(args, VIDEO_ENCODERS[video].args); }
  audio.forEach(function (x) {
    var enc = AUDIO_ENCODERS[x.codec];
    var s = 'a:' + (x.ordinal - 1);
    args.push('-c:' + s, enc.encoder);
    var br = x.bitrate || enc.bitrate;
    if (br) args.push('-b:' + s, br);
    if (x.sampleRate) args.push('-ar:' + s, String(x.sampleRate));
    if (x.channels) args.push('-ac:' + s, String(x.channels));
  });
  if (container === 'MPEG-4' && list.some(function (a) { return a.type === 'change-container'; })) {
    args.push('-movflags', '+faststart');
  }
  return args;
}

// Une liste de lignes par étape.
function render(steps, shell) {
  return steps.map(function (s) {
    var lines = s.pre.map(function (p) {
      // Copy-Item lit « \ » comme un séparateur ; la copie .NET prend le nom tel quel.
      return shell === 'powershell'
        ? '[System.IO.File]::Copy(' + psPath(p.copy.from) + ', ' + psPath(p.copy.to) + ')'
        : 'cp -- ' + quote(fileArg(p.copy.from), shell) + ' ' + quote(fileArg(p.copy.to), shell);
    });
    lines.push(line(s.tokens, shell));
    return { nature: s.nature, tool: s.tool, inPlace: s.inPlace, output: s.output, lines: lines };
  });
}

function natureLabel(nature) { return nature === 'transform' ? LABELS.reencodes : LABELS.lossless; }

function detectEnvironment(nav) {
  var p = nav && ((nav.userAgentData && nav.userAgentData.platform) || nav.platform || nav.userAgent) || '';
  return /win/i.test(p) ? 'powershell' : 'posix';
}

var api = {
  ACTIONS: ACTIONS, LABELS: LABELS, ENVIRONMENTS: ENVIRONMENTS, WARNINGS: WARNINGS,
  LANGUAGES: LANGUAGES, CONTAINERS: CONTAINERS, NATURE_ORDER: NATURE_ORDER,
  TOOLS: TOOLS, actionKey: actionKey, title: title, fill: fill, warningsOf: warningsOf,
  COPY_CONTAINERS: COPY_CONTAINERS, COPY_REASONS: COPY_REASONS, copyHandles: copyHandles, copyPlan: copyPlan,
  propose: propose, ready: ready, explain: explain, beforeAfter: beforeAfter,
  plan: plan, render: render, toolFor: toolFor, natureLabel: natureLabel, detectEnvironment: detectEnvironment,
  quotePosix: quotePosix, quotePowerShell: quotePowerShell, outputName: outputName
};
globalThis.MediaActions = api;
export default api;
